import { Link, usePage } from '@inertiajs/react'
import type { ReactNode } from 'react'
import FlashMessages from '../components/FlashMessages'
import LabMark from '../components/LabMark'
import { eyebrowClass, linkClass } from '../components/ui'
import type { Flash, SharedProps } from '../types'

const navClass = 'min-h-11 items-center rounded-sm text-sm font-medium text-ink transition-colors hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand'

export default function AppLayout({ children }: { children: ReactNode }) {
  const page = usePage<SharedProps>()
  const { current_user } = page.props
  // Inertia Rails exposes flash at the top level of the page object.
  const flash: Flash = page.flash ?? {}

  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink antialiased">
      <a href="#main-content" className="sr-only rounded-md bg-ink px-4 py-3 text-white focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand">
        Skip to content
      </a>
      <header className="border-b border-line">
        <nav aria-label="Main navigation" className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-4 sm:px-8 sm:py-5">
          <Link href="/" className="flex min-h-11 items-center gap-2 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand">
            <LabMark className="h-7 w-7 sm:h-8 sm:w-8" />
            <span className="font-serif text-lg font-semibold tracking-tight sm:text-2xl">Lie Angle Lab</span>
          </Link>
          <div className="flex flex-wrap items-center gap-3 sm:gap-7">
            {!current_user && <a href="/#approach" className={`${navClass} hidden md:inline-flex`}>The approach</a>}
            <a href="/#independence" className={`${navClass} hidden md:inline-flex`}>Our promise</a>
            <Link href="/shafts" className={`${navClass} inline-flex`} aria-current={page.url?.startsWith('/shafts') ? 'page' : undefined}>Shafts</Link>
            {current_user?.admin && <Link href="/admin/fix_list" className={`${navClass} inline-flex`} aria-current={page.url?.startsWith('/admin') ? 'page' : undefined}>Admin</Link>}
            {current_user ? (
              <>
                <Link href="/launch_sessions" className={`${navClass} inline-flex`} aria-current={page.url?.startsWith('/launch_sessions') ? 'page' : undefined}>Sessions</Link>
                <Link href="/profile/edit" className={`${navClass} inline-flex`} aria-current={page.url?.startsWith('/profile') ? 'page' : undefined}>My game</Link>
                <Link href="/session" method="delete" as="button" className={`${navClass} inline-flex`}>Sign out</Link>
              </>
            ) : (
              <>
                <Link href="/session/new" className={`${navClass} inline-flex`}>Sign in</Link>
                <Link href="/registration/new" className="inline-flex min-h-11 items-center justify-center rounded-md bg-brand px-3 py-2 sm:px-4 text-sm font-semibold text-white hover:bg-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand">Sign up free</Link>
              </>
            )}
          </div>
        </nav>
      </header>
      <main id="main-content" tabIndex={-1} className="mx-auto w-full max-w-6xl flex-1 px-5 py-10 sm:px-8 sm:py-14">
        <FlashMessages flash={flash} />
        {children}
      </main>
      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-6xl flex-col justify-between gap-5 px-5 py-8 sm:flex-row sm:items-center sm:px-8">
          <div>
            <p className="font-serif text-lg font-semibold">Lie Angle Lab</p>
            <p className="mt-1 text-sm text-muted">Understand your gear. Trust the evidence.</p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
            {current_user ? (
              <Link href="/" className={linkClass}>Your lab</Link>
            ) : (
              <a href="/#approach" className={linkClass}>How it works</a>
            )}
            <a href="/#independence" className={linkClass}>Independence</a>
            <span className={`${eyebrowClass} self-center text-muted`}>Built for golfers</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
